import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { connect } from 'react-redux';
import ReactSwipe from 'react-swipe';

import { AppContext } from '@prisjakt/ui-app-context';

import { logoutRequest } from '../../../account/actions';
import { AppState } from '../../../../reducers';
import * as actions from './actions';

import Menu from '../../../navigation/components/mobile-menu/menu';
import Categories from '../../../navigation/components/mobile-menu/components/categories';
import MobileMenuButton from '../../../navigation/components/mobile-menu-button';
import UserMenu from '../user-menu';
import List from '../../views/list';
import Lists from '../../views/lists';
import Alerts from '../../views/alerts';
import UserView from '../../views/user';
import SidebarHeader from './sidebar-header-mobile';

import { trackOpeningSidebar, trackClosingSidebar } from '../../tracking';

import * as S from './styles';

import { VIEWS } from './constants';
import { Category, View, CategoryMenuState } from '../../types';
import { User } from '../../../../types';
import { Draggable } from '../sharedTypes';

type OwnProps = {
  onClose: () => void;
  menuHistory: Category[];
  onSelectHistory: (id?: string) => void;
  categories: Category[];
  isTraversingUpwards: boolean;
  onMenuChange: (state: CategoryMenuState) => void;
  open: boolean;
};

type StateProps = {
  selectedList: number;
  isAuthenticated?: boolean;
  me: User;
  currentView: View;
};

type DispatchProps = {
  onLogout: (message: string) => void;
  setMobileSidebarView: (view: View) => void;
  openListsMobile: () => void;
  openAlertsMobile: () => void;
};

type Props = OwnProps & StateProps & DispatchProps;

// Utility functions to sort categories by id. The actual sorting method doesn't matter in this case, as long as it's stable.
// This is so that React's reconciliation algorithm will re-use any previously rendered elements.
const getCategoryId = (category: Category | null) =>
  (category && category.id) || '';
const compareById = (a: Category | null, b: Category | null) =>
  getCategoryId(a).localeCompare(getCategoryId(b), 'en');

const reactSwipeStyle = {
  child: {
    width: '100vw',
  },
  container: {},
  wrapper: {},
};

function SidebarMobile({
  categories,
  currentView,
  isAuthenticated,
  isTraversingUpwards,
  me,
  menuHistory,
  onClose,
  onLogout,
  onMenuChange,
  onSelectHistory,
  open,
  selectedList,
  setMobileSidebarView,
}: Props) {
  const { t } = useContext(AppContext);
  const [previousCategory, setPreviousCategory] = useState<Category | null>(
    null
  );
  const [viewportHeight, setViewportHeight] = useState<string>('100vh');
  const [draggable, setDraggable] = useState<Draggable>(false);
  const visible = open && categories && categories.length > 0;
  const currentCategory = menuHistory[menuHistory.length - 1];
  const parentCategory = menuHistory[menuHistory.length - 2];

  useEffect(() => {
    const updateViewportHeight = () => {
      if (!IS_BROWSER) return;

      // On mobile `vh` does not take browser UI into consideration,
      // causing 100vh to be higher than what is visible.
      // `window.innerHeight` is the actual visible height of the window
      // and will be updated every render.
      setViewportHeight(`${window.innerHeight}px`);
    };

    window.addEventListener('resize', updateViewportHeight);
    updateViewportHeight();

    return () => window.removeEventListener('resize', updateViewportHeight);
  }, [setViewportHeight]);

  const changeMenuLevel = useCallback(
    (category: Category) => {
      setPreviousCategory(currentCategory);
      onMenuChange({ categories: category.children, parent: category });
    },
    [currentCategory, onMenuChange, setPreviousCategory]
  );

  const openParentCategory = useCallback(() => {
    if (parentCategory) {
      setPreviousCategory(currentCategory);
      onSelectHistory(parentCategory.id);
    }
  }, [currentCategory, onSelectHistory, parentCategory]);

  const toggleDraggable = useCallback(() => {
    setDraggable(!draggable);
  }, [draggable]);

  const openView = useCallback(
    (view: View) => {
      trackOpeningSidebar(view);
      setDraggable(false);
      setMobileSidebarView(view);
    },
    [setDraggable, setMobileSidebarView]
  );

  const openRootView = useCallback(() => openView(VIEWS.ROOT), [openView]);

  const closeMenu = useCallback(() => {
    setPreviousCategory(null);
    onClose();
  }, [onClose, setPreviousCategory]);

  const closeMenuAndResetState = useCallback(() => {
    closeMenu();
    setMobileSidebarView(VIEWS.ROOT);
  }, [closeMenu, setMobileSidebarView]);

  const logoutClick = useCallback(() => {
    trackClosingSidebar(currentView, 'MobileLogoutButton');
    closeMenuAndResetState();
    onLogout(t('account', 'logout.successful'));
  }, [closeMenuAndResetState, onLogout, t, currentView]);

  const swipeOptions = useMemo(
    () => ({
      continuous: false,
      transitionEnd: (index: number) => {
        // Close menu on swipe away
        if (index > 0) {
          trackClosingSidebar(currentView, 'MobileSwipeToClose');
          closeMenuAndResetState();
        }
      },
    }),
    [closeMenuAndResetState, currentView]
  );

  return (
    <>
      <S.Overlay active={visible} />
      <S.FakeHeader visible={visible} />
      <S.Sidebar visible={visible}>
        <ReactSwipe swipeOptions={swipeOptions} style={reactSwipeStyle}>
          <S.SwipeTarget height={viewportHeight}>
            <S.MenuButtonArea visible={!visible}>
              <MobileMenuButton />
            </S.MenuButtonArea>
            <S.MenuButtonArea visible={visible}>
              <S.CloseButton aria-label={t('symbols', 'symbol.close')}>
                <S.Close />
              </S.CloseButton>
            </S.MenuButtonArea>
            <Menu
              closeMenu={closeMenuAndResetState}
              currentView={currentView}
              showSidebar
            >
              {/* Render the previous and the current category in order to be able to transition smoothly between states. */}
              {[previousCategory, currentCategory]
                .sort(compareById)
                .map((category: Category | null) => {
                  if (!category) {
                    return null;
                  }

                  const isCurrentCategory = category.id === currentCategory.id;
                  const isRootCategory = !category.name;

                  return (
                    <S.SidebarView
                      key={category.id}
                      visible={isCurrentCategory && currentView === VIEWS.ROOT}
                    >
                      {isRootCategory && (
                        <UserMenu
                          visible
                          onLinkClick={closeMenu}
                          onOpenView={openView}
                          me={me}
                          isAuthenticated={isAuthenticated}
                        />
                      )}
                      {!isRootCategory && (
                        <SidebarHeader
                          title={category.name}
                          onClickHandler={openParentCategory}
                        />
                      )}
                      <Categories
                        category={category}
                        changeMenuLevel={changeMenuLevel}
                        isTraversingUpwards={isTraversingUpwards}
                        closeMenu={closeMenu}
                        menuHistory={menuHistory}
                        previousCategory={previousCategory}
                        showSidebar
                      />
                    </S.SidebarView>
                  );
                })}

              <S.SidebarView key="lists" visible={currentView === VIEWS.LISTS}>
                <Lists
                  onOpenView={openView}
                  onLinkClick={closeMenu}
                  draggable={draggable}
                  toggleDraggable={toggleDraggable}
                  isMobile
                />
              </S.SidebarView>

              <S.SidebarView
                key={`list${selectedList}`}
                visible={currentView === VIEWS.LIST}
              >
                <List
                  id={selectedList}
                  onOpenView={openView}
                  onLinkClick={closeMenu}
                  draggable={draggable}
                  toggleDraggable={toggleDraggable}
                  isMobile
                />
              </S.SidebarView>

              <S.SidebarView
                key="alerts"
                visible={currentView === VIEWS.ALERTS}
              >
                <Alerts
                  onLinkClick={closeMenu}
                  onOpenView={openView}
                  isMobile
                />
              </S.SidebarView>

              {isAuthenticated && (
                <S.SidebarView key="user" visible={currentView === VIEWS.USER}>
                  <SidebarHeader
                    title={me.displayName || ''}
                    onClickHandler={openRootView}
                  />
                  <UserView
                    me={me}
                    closeMenu={closeMenu}
                    onLogoutClick={logoutClick}
                  />
                </S.SidebarView>
              )}
            </Menu>
          </S.SwipeTarget>
          <S.SwipeTarget />
        </ReactSwipe>
      </S.Sidebar>
    </>
  );
}

const mapStateToProps = (state: AppState) => ({
  selectedList: state.areas.sidebar.selectedList,
  me: state.areas.account.me,
  isAuthenticated: state.areas.account.isAuthenticated,
  currentView: state.areas.mobileSidebar.currentView,
});

const dispatchProps = {
  onLogout: logoutRequest,
  setMobileSidebarView: actions.setMobileSidebarView,
};

export default connect(mapStateToProps, dispatchProps)(SidebarMobile);
